import { useTranslation } from 'react-i18next'
import type {
  ManualStandardItem,
  StandardApplicability,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { StandardDataStatusBadge } from '@/components/StandardDataStatusBadge'
import { RequirementItem } from './RequirementItem'
import { useValidateStandardStatements } from './useValidateStandardStatements'

export function StandardSection({
  machineryDirectiveComplianceId,
  standardId,
  items,
  standardDefinition,
  documentId,
}: {
  machineryDirectiveComplianceId: string
  standardId: string
  items: ManualStandardItem[]
  standardDefinition: StandardApplicability
  documentId?: string
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const {
    isValidating,
    error,
    processingId,
    validationProgress,
    validationItems,
    validate,
  } = useValidateStandardStatements(
    machineryDirectiveComplianceId,
    standardId,
    documentId
  )

  const hasRequirements = items.length > 0
  const isDocumentSelected = Boolean(documentId)
  const buttonLabel = isDocumentSelected
    ? t('standardsPage.manualValidationPage.validate')
    : t('standardsPage.manualValidationPage.selectDocumentButton')
  const buttonTitle = !isDocumentSelected
    ? t('standardsPage.manualValidationPage.selectDocumentHint')
    : undefined

  const handleValidate = () => {
    if (!isDocumentSelected) {
      return
    }
    validate()
  }

  const standardHasPdf = standardDefinition.hasFile === true

  const standardHasData =
    standardDefinition.hasSignificantHazards === true ||
    standardDefinition.hasInformationForUse === true ||
    standardDefinition.hasRiskReductionMeasures === true

  return (
    <Card>
      <CardHeader className='mb-2 flex flex-row flex-wrap items-center justify-between gap-4'>
        <div className='flex min-w-0 flex-1 items-start gap-2'>
          <CardTitle className='text-base wrap-break-word'>
            {standardDefinition.reference} – {standardDefinition.title}
          </CardTitle>
          <div>
            <StandardDataStatusBadge
              hasData={standardHasData}
              hasPdf={standardHasPdf}
              requireLinkedUserStandard
              userStandardId={standardDefinition.userStandardId}
            />
          </div>
        </div>

        {hasRequirements && (
          <div className='flex shrink-0 items-center gap-4'>
            {validationProgress && (
              <span className='text-sm font-medium text-muted-foreground'>
                {validationProgress.current} / {validationProgress.total}
              </span>
            )}
            <Button
              onClick={handleValidate}
              disabled={isValidating || !isDocumentSelected}
              title={buttonTitle}
              size='sm'
            >
              {isValidating ? (
                <span className='flex items-center gap-2'>
                  <span className='size-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
                  {buttonLabel}
                </span>
              ) : (
                buttonLabel
              )}
            </Button>
          </div>
        )}
      </CardHeader>
      {error && (
        <Alert variant='destructive' className='mb-4'>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!hasRequirements && (
        <CardContent>
          <p className='text-sm text-muted-foreground'>
            {t('standardsPage.manualValidationPage.noSpecificRequirements')}
          </p>
        </CardContent>
      )}
      {hasRequirements && (
        <ul className='list-none space-y-2 divide-y pb-2'>
          {items.map((item) => (
            <li
              key={item.id}
              className='relative flex min-w-0 gap-2 px-4 py-2 text-sm'
            >
              <RequirementItem
                item={item}
                validationItem={validationItems.get(item.id)}
                isProcessing={processingId === item.id}
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
